'use client';

import { useCallback, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

type UseDebouncedSearchInputOptions = {
  initialValue?: string;
  delay?: number;
  onCommit: (next: string) => void;
};

type UseDebouncedSearchInputResult = {
  inputValue: string;
  handleChange: (next: string) => void;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
};

const DEFAULT_DELAY_MS = 500;

// Shared list-page search pattern: keep an immediate `inputValue` for the
// controlled input, debounce a `onCommit(next)` (typically setSearchTerm +
// resetPagination), and cancel the pending call on unmount.
function useDebouncedSearchInput({
  initialValue = '',
  delay = DEFAULT_DELAY_MS,
  onCommit,
}: UseDebouncedSearchInputOptions): UseDebouncedSearchInputResult {
  const [inputValue, setInputValue] = useState(initialValue);

  const debouncedCommit = useDebouncedCallback((next: string) => {
    onCommit(next);
  }, delay);

  const handleChange = useCallback(
    (next: string) => {
      setInputValue(next);
      debouncedCommit(next);
    },
    [debouncedCommit],
  );

  // Programmatic resets (e.g. a "clear filters" button that also sets the
  // committed term directly) must cancel any pending debounced commit, or the
  // in-flight commit fires after the reset and re-applies the stale term,
  // desyncing the input from the committed value. handleChange keeps using the
  // raw setter so typing still debounces normally.
  const setInputValueAndCancel = useCallback<React.Dispatch<React.SetStateAction<string>>>(
    (next) => {
      debouncedCommit.cancel();
      setInputValue(next);
    },
    [debouncedCommit],
  );

  useEffect(() => {
    return () => {
      debouncedCommit.cancel();
    };
  }, [debouncedCommit]);

  return {
    inputValue,
    handleChange,
    setInputValue: setInputValueAndCancel,
  };
}

export default useDebouncedSearchInput;
